/**
 * Call History Card Component (Presentation)
 *
 * Compact card for a single call history item in the queue panel History tab.
 * Uses PersonDisplay for caller/callee.
 */

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';

import { PersonDisplay } from '@/shared/components';

import type { CallHistoryItem } from '../../domain/types/responses';

export interface CallHistoryCardProps {
  item: CallHistoryItem;
  onClick?: () => void;
}

function formatCallHistoryDate(dateString?: string | null): string {
  if (!dateString) return '—';

  // Keep current UI behavior, but ensure tests can mock this and avoid locale/timezone brittleness.
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatOutcome(outcome?: string | null): string {
  if (!outcome) return '—';
  return outcome.replace(/_/g, ' ');
}

export function CallHistoryCard({ item, onClick }: CallHistoryCardProps) {
  const isIncoming = item.call_type === 'incoming';
  /** Outgoing: show callee (customer); incoming: show caller (customer). */
  const displayPerson = isIncoming ? item.caller : item.callee;

  const outcome = formatOutcome(item.call_outcome ?? item.case_call_detail?.call_outcome);
  const createdDate = formatCallHistoryDate(item.call_time_start ?? item.created_at);

  const fallbackLabel = item.loan_id ?? item.case_display_id ?? '—';

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        width: '100%',
        textAlign: 'left',
        p: 1.5,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Call Direction Icon */}
      <Box
        sx={{
          color: isIncoming ? 'info.main' : 'success.main',
          mr: 1.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {isIncoming ? <PhoneCallbackIcon fontSize="small" /> : <PhoneForwardedIcon fontSize="small" />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Enforced: name / outcome / date */}
        <Box sx={{ mb: 1 }}>
          <PersonDisplay person={displayPerson} variant="compact" avatarSize={28} fallbackLabel={fallbackLabel} />
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textTransform: 'capitalize',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
          }}
        >
          {outcome}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {createdDate}
        </Typography>
      </Box>
    </ButtonBase>
  );
}
